import Box from "@mui/material/Box";
import CallDetailsTab from "./MonitoringCallInfo/CallDetailsTab";
import React, { useContext, useEffect, useMemo, useState } from "react";
import TranscriptTab from "./Transcripts/TranscriptTab";
import VoiceOfTheCustomerSummaryTab from "./VoiceOfTheCustomerSummaryTab";
import FeedbackTab from "../Feedback/Shared/FeedbackTab";
import TranscriptSearch from "./Transcripts/TranscriptSearch";
import MonitoringScoreCard from "./MonitoringScoreCard";
import { APIContext } from "../Contexts/APIContext";
import useAIAnswers, { AIAnswerActions } from "./AIAnalysis/Hooks/useAIAnswers";
import TranscriptSummaryViewModel from "./Transcripts/TranscriptSummaryViewModel";
import AIAnswers from "./AIAnalysis/AIAnswers";
import { CallInfoTabsHaveValues } from "./ViewModels/CallInfoTabsHaveValues";

interface ICallTabPagesProps {
    id: string;
    setId: (id: string) => void;
    switchCallId?: string;
    index: number;
    agentView?: boolean;
    feedbackView?: string;
    transcriptSearch?: TranscriptSearch;
    completeId?: number;
    setRescoreCompleteId?: (completeId: number) => void;
    close?: () => void;
    agentHasResponded?: boolean;
    systemId: string;
    mainView?: boolean;
    isUsersCall?: boolean;
    toggleTimerActive: (active: boolean) => void;
    roleCanSeeScorecard: boolean;
    setCallInfoTabsHaveValues: (tabEnabled: CallInfoTabsHaveValues | null) => void;
}

export default function CallTabPages(props: ICallTabPagesProps) {
    const { isTeamLeader, isClient, isClientAIEnabled, isClientTranscriptsEnabled, get } = useContext(APIContext);
    const aiEnabled = !isClient || isClientAIEnabled;
    const transcriptsEnabled = !isClient || isClientTranscriptsEnabled;

    //#region tab content components
    const callId = props.switchCallId ?? props.id;

    //data needs to be set here else the tabs recall the data ever time the tab changes
    //when aiEnabled is false the tabs are still shown but disabled, so the hook skips the fetch (passing enabled keeps the hook call unconditional)
    const { answers: vocDetailsAnswers, isLoading: vocIsLoading } = useAIAnswers(callId, AIAnswerActions.getVocAnswers, aiEnabled);
    const { answers: insightsAnswers, isLoading: insightsIsLoading } = useAIAnswers(callId, AIAnswerActions.getInsightAnswers, aiEnabled);
    const { answers: salesAIAnswers, isLoading: salesAIIsLoading } = useAIAnswers(callId, AIAnswerActions.getSalesAnswers, aiEnabled);
    const { answers: guideAnswers, isLoading: guideIsLoading } = useAIAnswers(callId, AIAnswerActions.getGuideAnswers, aiEnabled);

    const [summary, setSummary] = useState<TranscriptSummaryViewModel | null>(null);
    const [summaryIsLoading, setSummaryIsLoading] = useState<boolean>(false);
    useEffect(() => {
        setSummary(null);

        if (aiEnabled) {
            setSummaryIsLoading(true);

            get('Transcript', `GetTranscriptSummary?switchCallId=${callId}`, (data) => {
                setSummary(data);
                setSummaryIsLoading(false);
            }, () => {
                setSummary(null);
                setSummaryIsLoading(false);
            })
        }
        else {
            setSummaryIsLoading(false);
        }
    }, [callId, aiEnabled])

    const callInfoTabsHaveValues = useMemo<CallInfoTabsHaveValues>(() => ({
        vocSummaryHasValues: !!summary,
        vocDetailsHasValues: vocDetailsAnswers != null && vocDetailsAnswers.length > 0,
        insightsHasValues: insightsAnswers != null && insightsAnswers.length > 0,
        salesAIHasValues: salesAIAnswers != null && salesAIAnswers.length > 0,
        guideHasValues: guideAnswers != null && guideAnswers.length > 0,
    }), [summary, vocDetailsAnswers, insightsAnswers, salesAIAnswers, guideAnswers]);

    const allLoaded = !vocIsLoading && !insightsIsLoading && !salesAIIsLoading && !guideIsLoading && !summaryIsLoading;

    useEffect(() => {
        //only set the callInfoTabsHaveValues when all tabs have finished loading
        if (allLoaded)
            props.setCallInfoTabsHaveValues(callInfoTabsHaveValues);
    }, [callInfoTabsHaveValues, allLoaded]);
    //#endregion

    //make sure tabs are in the same order as the array in SecondaryCallTabs
    let tabs = [
        ...props.feedbackView ? [{
            content:
                <FeedbackTab
                    view={props.feedbackView}
                    callRefId={props.id}
                    setCallRefId={props.setId}
                    completeId={props.completeId!}
                    setRescoreCompleteId={props.setRescoreCompleteId!}
                    close={props.close!}
                    agentHasResponded={props.agentHasResponded}
                />,
            name: "Feedback"
        }] : [],
        { content: <VoiceOfTheCustomerSummaryTab id={props.id!} switchCallId={props.switchCallId} summary={summary} />, name: "Voice of the Customer Summary" },
        { content: <AIAnswers answers={vocDetailsAnswers} isLoading={vocIsLoading} />, name: "Voice of the Customer Details" },
        { content: <AIAnswers answers={insightsAnswers} isLoading={insightsIsLoading} />, name: "Insight" },
        { content: <AIAnswers answers={salesAIAnswers} isLoading={salesAIIsLoading} />, name: "Sales AI" },
        { content: <AIAnswers answers={guideAnswers} isLoading={guideIsLoading} />, name: "Guide" },
        { content: <CallDetailsTab id={props.id} setId={props.setId} agentView={props.agentView} systemId={props.systemId} />, name: "Call Details" },
        ... !props.agentView && props.roleCanSeeScorecard && (props.mainView || transcriptsEnabled) ? [{
            content: props.mainView ?
                []
                :
                [<TranscriptTab id={props.id!} switchCallId={props.switchCallId} transcriptSearch={props.transcriptSearch} />],
            name: props.mainView ? "Scorecard" : "Transcript"
        }] : [],
    ]

    return (
        <>
            {tabs[props.index].name !== 'Scorecard' &&
                <Box flexGrow={1} overflow="hidden" sx={{ height: '100%', backgroundColor: '#fff' }}>
                    {
                        tabs[props.index].content
                    }
                </Box>
            }
            {(!props.agentView && props.roleCanSeeScorecard && props.mainView) &&
                <Box
                    flexGrow={1}
                    overflow="hidden"
                    sx={tabs[props.index].name === "Scorecard" ? { height: '100%', backgroundColor: '#fff' } : { width: '0px', height: '0px', visibility: 'hidden' }}
                >
                    <MonitoringScoreCard
                        callRefId={props.id}
                        agentView={props.agentView}
                        isUsersCall={props.isUsersCall}
                        isTeamLeader={isTeamLeader}
                        toggleTimerActive={props.toggleTimerActive}
                        mainView={props.mainView}
                    />
                </Box>
            }
        </>
    );
}